/**
 * Platform Config > Navigation Menu Management > Dashboard > "Per-site tab
 * visibility" (CR-9099).
 *
 * The Dashboard tab toggles above this panel are the default for every site.
 * Here one customer site can override a tab: Show, Hide, or Follow global. A
 * tab left on Follow global tracks the toggles above, including later changes.
 * Scheduled Dashboards and Spot Orders can only be hidden for a site: both are
 * switched on for the whole install, and Scheduled also switches on OTIF
 * Exclusions and GRN Repair. Each row says why (HIDE_ONLY_REASONS).
 *
 * Each change saves immediately (one PATCH per change), independent of the
 * Save Tab Visibility button above. Helix operators only.
 */
import { useCallback, useEffect, useState } from 'react';
import { platformSettingsAPI, type DashboardTabSiteRow, type DashboardTabOverrideChanges } from '../../../lib/api';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { DASHBOARD_TAB_REGISTRY } from '../../dashboard/types';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { MapPin, Loader2, RefreshCw, AlertCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

type OverrideChoice = 'global' | 'show' | 'hide';

const CHOICE_TO_VALUE: Record<OverrideChoice, boolean | null> = { global: null, show: true, hide: false };

// Why a site may hide these tabs but never switch them on (the server's hide_only list).
const HIDE_ONLY_REASONS: Record<string, string> = {
  'scheduled': 'Can only be hidden per site, because it also switches on OTIF Exclusions and GRN Repair for the whole install.',
  'spot-orders': 'Can only be hidden per site, because it is switched on for the whole install, not site by site.',
};
const HIDE_ONLY_FALLBACK = 'Can only be hidden per site, because it is switched on for the whole install.';

// The Overridden badge: light green for a tab the site shows, light red for one it hides.
const OVERRIDE_SHOW_CLASS = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
const OVERRIDE_HIDE_CLASS = 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';

interface DashboardTabSitePanelProps {
  /** The tab toggles above as they stand (possibly unsaved), for the Follow global label. */
  platformTabs: Record<string, boolean>;
}

function choiceOf(override: boolean | undefined): OverrideChoice {
  if (override === undefined) return 'global';
  return override ? 'show' : 'hide';
}

function overrideCount(site: DashboardTabSiteRow): number {
  return Object.keys(site.overrides).length;
}

/** The server's reason for a refused save (core.exceptions envelope), else a generic line. */
function saveErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: string; details?: { fields?: Record<string, string> } } } } | null)
    ?.response?.data;
  const fields = data?.details?.fields;
  if (fields && Object.keys(fields).length > 0) return Object.values(fields).join(' ');
  return data?.message ?? 'The change was not saved. Check your connection and try again.';
}

export function DashboardTabSitePanel({ platformTabs }: DashboardTabSitePanelProps) {
  const { loadSettingsFromBackend } = useMenuSettings();
  const [sites, setSites] = useState<DashboardTabSiteRow[]>([]);
  const [hideOnly, setHideOnly] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformSettingsAPI.getDashboardTabSiteOverrides();
      const rows = res.data.sites ?? [];
      setSites(rows);
      setHideOnly(res.data.hide_only ?? []);
      setSelectedId(prev => (prev !== null && rows.some(s => s.id === prev) ? prev : rows[0]?.id ?? null));
    } catch (err: unknown) {
      console.error('Failed to load per-site dashboard tabs:', err);
      setError('Could not load the per-site dashboard tabs. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const site = sites.find(s => s.id === selectedId) ?? null;

  const save = async (changes: DashboardTabOverrideChanges, successMessage: string) => {
    if (!site) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await platformSettingsAPI.updateDashboardTabSiteOverrides(site.id, changes);
      const { overrides, effective } = res.data;
      setSites(prev => prev.map(s => (s.id === site.id ? { ...s, overrides, effective } : s)));
      toast.success(successMessage);
      // The viewer may be looking at this site: refresh their own menus and tabs.
      await loadSettingsFromBackend();
    } catch (err: unknown) {
      console.error('Failed to update per-site dashboard tabs:', err);
      toast.error('Failed to update the dashboard tabs for this site');
      setSaveError(saveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const onChoice = (tabId: string, tabLabel: string, choice: OverrideChoice) => {
    if (!site) return;
    const label = choice === 'global' ? 'follows global' : choice === 'show' ? 'shown' : 'hidden';
    save({ [tabId]: CHOICE_TO_VALUE[choice] }, `${tabLabel} ${label} for ${site.name}`);
  };

  const onReset = () => {
    if (!site) return;
    const changes: DashboardTabOverrideChanges = Object.fromEntries(
      Object.keys(site.overrides).map(tabId => [tabId, null]),
    );
    save(changes, `${site.name} now follows the global dashboard tabs`);
  };

  return (
    <div className="ml-6 pl-4 border-l-2 border-muted" data-testid="dashboard-tab-site-panel">
      <div className="p-3 border rounded-lg space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-primary mt-0.5" />
            <div>
              <Label className="text-sm font-medium">Per-site tab visibility</Label>
              <p className="text-xs text-muted-foreground">
                Override the tabs above for one customer site. Follow global keeps the site on the
                toggles above. Group and division views always use the toggles above. Hiding
                Scheduled Dashboards for a site also removes OTIF Exclusions and GRN Repair there.
                Changes save immediately.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading || saving} aria-label="Reload per-site dashboard tabs">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>

        {loading && sites.length === 0 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading sites…
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-xs text-destructive py-2" role="alert">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        )}

        {!loading && !error && sites.length === 0 && (
          <p className="text-xs text-muted-foreground py-2">No customer sites on this install yet.</p>
        )}

        {site && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={String(site.id)}
                onValueChange={(v) => { setSelectedId(Number(v)); setSaveError(null); }}
                disabled={saving}
              >
                <SelectTrigger className="w-[280px]" aria-label="Site">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(s => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.name}{!s.is_active ? ' (inactive)' : ''}
                      {overrideCount(s) > 0 ? ` · ${overrideCount(s)} overridden` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={onReset}
                disabled={saving || overrideCount(site) === 0}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset site to global
              </Button>
              {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>

            {saveError && (
              <div className="flex items-center gap-2 text-xs text-destructive" role="alert" data-testid="dashboard-tab-site-save-error">
                <AlertCircle className="h-4 w-4" /> {saveError}
              </div>
            )}

            <div className="space-y-2">
              {DASHBOARD_TAB_REGISTRY.map(tab => {
                const override = site.overrides[tab.id];
                const isHideOnly = hideOnly.includes(tab.id);
                const globalOn = platformTabs[tab.id] ?? false;
                // A hide-only tab that is off for every site has nothing to hide.
                const locked = isHideOnly && !globalOn && override === undefined;
                return (
                  <div
                    key={tab.id}
                    data-testid={`dashboard-tab-site-row-${tab.id}`}
                    className="flex flex-wrap items-center justify-between gap-2 p-2 border rounded-lg"
                  >
                    <div className="min-w-0 flex-1 basis-40">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs font-medium">{tab.label}</Label>
                        {locked && <span className="text-xs text-muted-foreground">Off for every site</span>}
                      </div>
                      {isHideOnly && (
                        <p className="text-xs text-muted-foreground">{HIDE_ONLY_REASONS[tab.id] ?? HIDE_ONLY_FALLBACK}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {override !== undefined && (
                        <Badge
                          variant="outline"
                          className={`text-xs border-transparent ${override ? OVERRIDE_SHOW_CLASS : OVERRIDE_HIDE_CLASS}`}
                        >
                          Overridden
                        </Badge>
                      )}
                      <Select
                        value={choiceOf(override)}
                        onValueChange={(v) => onChoice(tab.id, tab.label, v as OverrideChoice)}
                        disabled={saving || locked}
                      >
                        <SelectTrigger className="w-[180px] h-8 text-xs" aria-label={`${tab.label} for ${site.name}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="global">Follow global ({globalOn ? 'On' : 'Off'})</SelectItem>
                          {!isHideOnly && <SelectItem value="show">Show</SelectItem>}
                          <SelectItem value="hide">Hide</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DashboardTabSitePanel;
